import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import {
  Settings, Users, MapPin, Clock, Stethoscope, Save,
  LocateFixed, Plus, Pencil, Trash2, Home, Navigation2,
  BarChart3, History, RefreshCw, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export const ConfigSistema = ({
  isAdmin,
  systemSettings,
  setSystemSettings,
  settingsLoading,
  savingSettings,
  handleSaveSettings,
  geocodingBase,
  handleGeocodeBase,
  equipes,
  loadingEquipes,
  deletingEquipe,
  handleOpenEquipeModal,
  handleDeleteEquipe,
  api
}) => {
  if (!isAdmin) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Configurações do Sistema
        </CardTitle>
        <CardDescription>
          Configurações globais que afetam todo o sistema
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {settingsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Equipe Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Gerenciamento de Equipes
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenEquipeModal()}
                  className="gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Nova Equipe
                </Button>
              </div>
              
              {loadingEquipes ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-2">
                  {equipes.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhuma equipe cadastrada. Clique em &quot;Nova Equipe&quot; para criar.
                    </p>
                  ) : (
                    equipes.map((equipe) => (
                      <div
                        key={equipe.id}
                        className="flex items-center justify-between p-3 rounded-lg border bg-card"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: equipe.cor }}
                          />
                          <div>
                            <p className="font-medium">{equipe.nome}</p>
                            <p className="text-xs text-muted-foreground">
                              Capacidade: {equipe.capacidade} pacientes | {equipe.total_pacientes || 0} atual
                            </p>
                            {equipe.tecnico_nome && (
                              <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1 mt-0.5">
                                <Home className="h-3 w-3" />
                                Técnico: {equipe.tecnico_nome}
                                {equipe.tecnico_lat ? ' (localizado)' : ' (sem localização)'}
                              </p>
                            )}
                            {equipe.ponto_inicial_tipo && equipe.ponto_inicial_tipo !== 'BASE' && (
                              <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1 mt-0.5">
                                <Navigation2 className="h-3 w-3" />
                                Início: {equipe.ponto_inicial_tipo === 'TECNICO' ? 'Casa do técnico' : 'Personalizado'}
                              </p>
                            )}
                            {equipe.ponto_final_tipo && equipe.ponto_final_tipo !== 'BASE' && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-0.5">
                                <Navigation2 className="h-3 w-3" />
                                Fim: {equipe.ponto_final_tipo === 'TECNICO' ? 'Casa do técnico' : 'Personalizado'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEquipeModal(equipe)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteEquipe(equipe.id)}
                            disabled={deletingEquipe === equipe.id}
                          >
                            {deletingEquipe === equipe.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Base Address Settings */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Endereço Base (Lifeport)
              </h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="base_endereco">Endereço Completo</Label>
                  <div className="flex gap-2">
                    <Input
                      id="base_endereco"
                      value={systemSettings.base_endereco}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        base_endereco: e.target.value
                      }))}
                      placeholder="Rua, número, bairro, cidade - UF"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={handleGeocodeBase}
                      disabled={geocodingBase || !systemSettings.base_endereco}
                    >
                      {geocodingBase ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <LocateFixed className="h-4 w-4" />
                      )}
                      <span className="ml-2 hidden sm:inline">Localizar</span>
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="base_lat">Latitude</Label>
                    <Input
                      id="base_lat"
                      type="number"
                      step="0.0001"
                      value={systemSettings.base_lat}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        base_lat: parseFloat(e.target.value) || 0
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="base_lng">Longitude</Label>
                    <Input
                      id="base_lng"
                      type="number"
                      step="0.0001"
                      value={systemSettings.base_lng}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        base_lng: parseFloat(e.target.value) || 0
                      }))}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Este endereço é usado como ponto de partida para cálculo de rotas
                </p>
              </div>
            </div>

            <Separator />

            {/* Capacidade Total */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Capacidade Total de Pacientes
              </h3>
              <div className="space-y-2">
                <Label htmlFor="capacidade_total">Capacidade total (leitos/vagas)</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="capacidade_total"
                    type="number"
                    min="0"
                    value={systemSettings.capacidade_total}
                    onChange={(e) => setSystemSettings(prev => ({
                      ...prev,
                      capacidade_total: parseInt(e.target.value) || 0
                    }))}
                    className="w-32"
                    data-testid="input-capacidade-total"
                  />
                  <p className="text-xs text-muted-foreground">
                    {systemSettings.capacidade_total > 0
                      ? 'Valor personalizado usado nos KPIs'
                      : 'Usando soma das capacidades das equipes'}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Defina 0 para usar automaticamente a soma das capacidades das equipes
                </p>
              </div>
            </div>

            <Separator />

            {/* Backfill de Histórico */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <History className="h-4 w-4" />
                Histórico de Equipes/Médicos
              </h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Gerar registros de histórico para pacientes antigos que ainda não possuem entradas de rastreamento.
                </p>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await api.post('/historico/backfill');
                      toast.success(res.data.message);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erro ao executar backfill');
                    }
                  }}
                  data-testid="btn-backfill-historico"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Gerar histórico para pacientes antigos
                </Button>
              </div>
            </div>

            <Separator />

            {/* Automatic Task Settings */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Stethoscope className="h-4 w-4" />
                Tarefa Automática de Enfermagem
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Ativar tarefa automática</Label>
                    <p className="text-xs text-muted-foreground">
                      Criar &quot;Visita enfermagem&quot; diária para todos pacientes internados
                    </p>
                  </div>
                  <Switch
                    checked={systemSettings.tarefa_automatica_enfermagem}
                    onCheckedChange={(checked) => setSystemSettings(prev => ({
                      ...prev,
                      tarefa_automatica_enfermagem: checked
                    }))}
                  />
                </div>
                
                {systemSettings.tarefa_automatica_enfermagem && (
                  <div className="space-y-2">
                    <Label htmlFor="horario_enfermagem" className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Horário padrão
                    </Label>
                    <Input
                      id="horario_enfermagem"
                      type="time"
                      value={systemSettings.horario_tarefa_enfermagem}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        horario_tarefa_enfermagem: e.target.value
                      }))}
                      className="w-32"
                    />
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSettings}
                disabled={savingSettings}
              >
                {savingSettings ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar Configurações
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
